import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useSite } from "@/context/SiteContext";
import { PageHero, Reveal } from "@/components/site/Reveal";
import { usePageMeta } from "@/lib/seo";

function Lightbox({ items, index, setIndex }) {
  const item = index != null ? items[index] : null;
  const step = (d) => setIndex((index + d + items.length) % items.length);
  return (
    <Dialog open={item != null} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent className="max-w-5xl border-white/10 bg-black p-2 sm:p-4" data-testid="gallery-lightbox" aria-describedby={undefined}
        onKeyDown={(e) => { if (e.key === "ArrowRight") step(1); if (e.key === "ArrowLeft") step(-1); }}>
        <DialogTitle className="sr-only">{item?.alt}</DialogTitle>
        {item && (
          <div className="relative">
            <img src={item.src} alt={item.alt} className="max-h-[78vh] w-full rounded-sm object-contain" data-testid="lightbox-image" />
            <button onClick={() => step(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/70 p-2 text-white hover:text-[var(--gold)]" aria-label="Previous" data-testid="lightbox-prev-btn"><ChevronLeft className="h-6 w-6" /></button>
            <button onClick={() => step(1)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/70 p-2 text-white hover:text-[var(--gold)]" aria-label="Next" data-testid="lightbox-next-btn"><ChevronRight className="h-6 w-6" /></button>
            <div className="flex justify-between px-1 pt-3 text-sm text-white/70">
              <span>{item.alt}</span><span className="font-mono-ui text-xs">{index + 1} / {items.length}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Gallery() {
  const { content } = useSite();
  const [cat, setCat] = useState("All");
  const [index, setIndex] = useState(null);
  usePageMeta("Gym Photos | Iron Pump Gym Bayside & Flushing Queens", "See inside Iron Pump Gym: the gym floor, equipment and community at our Bayside and Flushing, Queens locations.");
  const cats = ["All", ...new Set(content.gallery.map((g) => g.category))];
  const items = cat === "All" ? content.gallery : content.gallery.filter((g) => g.category === cat);
  return (
    <>
      <PageHero eyebrow="Gallery" title="Inside Iron Pump" sub="The floor, the iron and the people who make it." image="/images/bench.webp" />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8">
        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-1.5 font-display text-base font-bold uppercase tracking-wide transition-colors duration-200 ${cat === c ? "border-[var(--gold)] bg-[var(--gold)] text-black" : "border-white/15 text-white/70 hover:border-white/40"}`}>{c}</button>
          ))}
        </div>
        <div className="mt-8 columns-2 gap-3 md:columns-3" data-testid="gallery-grid">
          {items.map((g, i) => (
            <Reveal key={g.src + i} delay={(i % 3) * 0.06} className="mb-3 break-inside-avoid">
              <button onClick={() => setIndex(i)} className="group relative block w-full overflow-hidden rounded-sm" data-testid={`gallery-item-${i}`}>
                <img src={g.src} alt={g.alt} loading="lazy" decoding="async" className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${i % 3 === 0 ? "aspect-[3/4]" : "aspect-square"}`} />
                <span className="absolute bottom-2 left-2 rounded-sm bg-black/70 px-2 py-0.5 font-mono-ui text-[10px] uppercase tracking-wider text-[var(--gold)] opacity-0 transition-opacity duration-300 group-hover:opacity-100">{g.category}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>
      <Lightbox items={items} index={index} setIndex={setIndex} />
    </>
  );
}
